<template>
  <div class="sf-layer-white sf-layer-border">
    <div class="sf-pad-3 sf-layer-border-bottom">
      <div class="sf-font-l sf-title sf-below-3">{{ title }}</div>
      <AdjustmentsSidebarSelect
        v-if="needsAccount"
        v-model="selectedAccountId"
        name="period"
        :is-disabled="isLoadingLedger"
        :options="forwardAccountSelectOptions"
        :placeholder-label="I18n.t('adjustment.selective_copy.select_account')"
        :title="I18n.t('adjustment.selective_copy.forward_account')"
      />
      <AdjustmentsSidebarSelect
        v-model="selectedLedgerId"
        name="period"
        :is-disabled="isLedgerDisabled"
        :options="ledgerSelectOptions"
        :placeholder-label="I18n.t('adjustment.selective_copy.select_period')"
        :title="I18n.t('adjustment.selective_copy.from')"
      />
      <span class="sf-inline-block sf-pad-r-1">{{
        I18n.t('adjustment.selective_copy.to')
      }}</span>
      <b class="sf-font-m">{{ currentLedgerPeriodName }}</b>
    </div>
    <div v-if="hasLedgerSelected" class="sf-pad-3 sf-layer-border-bottom">
      <div class="sf-uppercase sf-below-2">
        {{ I18n.t('adjustment.selective_copy.category') }}
      </div>
      <FilterTag
        v-for="category in translatedCategories"
        :key="category.id"
        :count-active="
          getCheckedAdjustmentIdsByFilterLength.categoryIds[category.id] ?? 0
        "
        :count-total="
          getAdjustmentIdsByFilterLength.categoryIds[category.id] ?? 0
        "
        :filter-item="category"
        adjustment-key="categoryIds"
        @toggle-tag="toggleTag"
      ></FilterTag>
    </div>
    <div v-if="shouldShowTags" class="sf-pad-3 sf-layer-border-bottom">
      <div class="sf-uppercase sf-below-2">
        {{ I18n.t('adjustment.selective_copy.tags') }}
      </div>
      <FilterTag
        v-for="tag in tags"
        :key="tag.id"
        :count-active="
          getCheckedAdjustmentIdsByFilterLength.tagIds[tag.id] ?? 0
        "
        :count-total="getAdjustmentIdsByFilterLength.tagIds[tag.id] ?? 0"
        :filter-item="tag"
        adjustment-key="tagIds"
        @toggle-tag="toggleTag"
      ></FilterTag>
    </div>
    <div v-if="shouldShowCopyTypes" class="sf-pad-3 sf-layer-border-bottom">
      <div class="sf-uppercase sf-below-2">
        {{ I18n.t('adjustment.selective_copy.copy_type') }}
      </div>
      <FilterTag
        v-for="ctype in translatedCopyTypes"
        :key="ctype.id"
        :count-active="
          getCheckedAdjustmentIdsByFilterLength.copyTypes[ctype.id] ?? 0
        "
        :count-total="getAdjustmentIdsByFilterLength.copyTypes[ctype.id] ?? 0"
        :filter-item="ctype"
        adjustment-key="copyTypes"
        @toggle-tag="toggleTag"
      ></FilterTag>
    </div>
    <div v-show="hasLedgerSelected" class="sf-pad-3 sf-layer-border-bottom">
      <div
        v-dompurify-html="totalReplaced"
        class="sf-p sf-below-3 sf-line-height"
      ></div>
      <a
        :class="submitClass"
        class="sf-btn sf-btn-success"
        href="#"
        @click.prevent="submitForm"
        >{{ submitLabel }}</a
      >
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'

import AdjustmentsSidebarSelect from './adjustments-sidebar-select.vue'
import FilterTag from './filter-tag.vue'
import { useAdjustmentsStore } from '../store'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'AdjustmentsSidebar',
})

interface Props {
  copyType: 'forward' | 'reverse' | 'normal'
  currentLedgerPeriodName: string
  totalReplacedHtml: string
}

const props = withDefaults(defineProps<Props>(), {
  copyType: 'forward',
})

const adjustmentsStore = useAdjustmentsStore()

const {
  categories,
  copyTypes,
  forwardAccounts,
  getAdjustmentsLength,
  getAdjustmentIdsByFilterLength,
  getCheckedAdjustments,
  getCheckedAdjustmentsLength,
  getCheckedAdjustmentIdsByFilterLength,
  hasLedgerSelected,
  isLedgerDisabled,
  isLoadingLedger,
  isSubmitDisabled,
  ledgers,
  selectedAccountId,
  selectedLedgerId,
  shouldShowCopyTypes,
  shouldShowTags,
  tags,
} = storeToRefs(adjustmentsStore)

const { fetchAdjustments, submitForm, toggleTag } = adjustmentsStore

const I18n = useI18n()

const title = computed(() =>
  I18n.t(`adjustment.selective_copy.${props.copyType}_title`)
)

const needsAccount = computed(() => props.copyType === 'forward')

const forwardAccountSelectOptions = computed(() => {
  return forwardAccounts.value.map((account) => ({
    label: account.name,
    value: account.id,
    selected: account.id === selectedAccountId.value,
  }))
})

const ledgerSelectOptions = computed(() => {
  return ledgers.value.map((ledger) => ({
    label: ledger.period_name,
    value: ledger.id,
    selected: ledger.id === selectedLedgerId.value,
  }))
})

const submitClass = computed(() => {
  return {
    'sf-btn-disabled': isSubmitDisabled.value,
  }
})

const submitLabel = computed(() =>
  I18n.t(`adjustment.selective_copy.${props.copyType}_title`)
)

const totalReplaced = computed(() => {
  if (!props.totalReplacedHtml) {
    return ''
  }

  return props.totalReplacedHtml
    .replace(
      /{{\s*countTotalChecked\s*}}/g,
      getCheckedAdjustmentsLength.value.toString()
    )
    .replace(/{{\s*countTotal\s*}}/g, getAdjustmentsLength.value.toString())
})

const translatedCategories = computed(() =>
  categories.value.map((category) => ({
    ...category,
    name: I18n.t(`js.adjustments.category.${category.name}`),
  }))
)

const translatedCopyTypes = computed(() =>
  copyTypes.value.map((type) => ({
    ...type,
    name: I18n.t(`js.adjustments.copy_type.${type.name}`),
  }))
)

watch(selectedLedgerId, (ledgerId) => {
  if (ledgerId && ledgerId !== 'placeholder') {
    fetchAdjustments(
      ledgerId,
      selectedAccountId.value === 'placeholder'
        ? undefined
        : selectedAccountId.value
    )
  }
})

watch(selectedAccountId, (accountId) => {
  if (
    accountId &&
    accountId !== 'placeholder' &&
    selectedLedgerId.value !== 'placeholder'
  ) {
    fetchAdjustments(
      selectedLedgerId.value,
      accountId,
      getCheckedAdjustments.value
    )
  }
})
</script>
